import { Link } from 'react-router-dom';
import { LegalLayout } from '../components/LegalLayout';
import '../legal.css';

interface TierRow {
  tier: string;
  monthlyUptimeTarget: string;
  monthlyDowntimeBudget: string;
  supportResponseSev1: string;
}

const TIERS: TierRow[] = [
  {
    tier: 'Free',
    monthlyUptimeTarget: 'Best effort — no contractual target.',
    monthlyDowntimeBudget: '—',
    supportResponseSev1: 'Best effort via support@rynora.app.',
  },
  {
    tier: 'Team',
    monthlyUptimeTarget: '99.5% monthly availability of the run-execution surface.',
    monthlyDowntimeBudget: '~3h 39m / month.',
    supportResponseSev1: '1 business day.',
  },
  {
    tier: 'Business',
    monthlyUptimeTarget: '99.9% monthly availability of the run-execution surface.',
    monthlyDowntimeBudget: '~43m / month.',
    supportResponseSev1: '4 business hours.',
  },
  {
    tier: 'Enterprise',
    monthlyUptimeTarget: '99.95% monthly availability of the run-execution surface.',
    monthlyDowntimeBudget: '~21m / month.',
    supportResponseSev1: '1 hour, 24×7, with a named Technical Account Manager.',
  },
  {
    tier: 'Sovereign / self-hosted',
    monthlyUptimeTarget: 'Customer-managed — Rynora SLA applies only to the artefact build + support response.',
    monthlyDowntimeBudget: '—',
    supportResponseSev1: "Per the customer's sovereign support contract.",
  },
];

interface CreditRow {
  monthlyUptime: string;
  serviceCredit: string;
}

const SERVICE_CREDIT_LADDER: CreditRow[] = [
  { monthlyUptime: '< 99.9% but ≥ 99.0%', serviceCredit: '10% of the monthly fee for the affected org.' },
  { monthlyUptime: '< 99.0% but ≥ 95.0%', serviceCredit: '25% of the monthly fee for the affected org.' },
  { monthlyUptime: '< 95.0%', serviceCredit: '50% of the monthly fee for the affected org.' },
];

export function SlaPage() {
  return (
    <LegalLayout
      title="Service Level Agreement (SLA)"
      updated="June 1, 2026"
      summary="Per-tier monthly availability targets, what counts as downtime, and the service-credit ladder when we miss. Real-time and historical uptime live on the public status page."
      needsReview={true}
    >
      <h2>What this page commits to</h2>
      <p>
        Rynora commits to the monthly availability targets in the table below for each paid tier, measured against the
        run-execution surface (the ability to create, run, and read results from an authored test flow). Real-time and
        historical availability is published on the <Link to="/status">public status page</Link>; that page is the
        authoritative measurement source for SLA calculations.
      </p>

      <h2>Per-tier targets</h2>
      <table>
        <thead>
          <tr>
            <th>Tier</th>
            <th>Monthly uptime target</th>
            <th>Monthly downtime budget</th>
            <th>Sev-1 support response</th>
          </tr>
        </thead>
        <tbody>
          {TIERS.map((row) => (
            <tr key={row.tier}>
              <td>
                <strong>{row.tier}</strong>
              </td>
              <td>{row.monthlyUptimeTarget}</td>
              <td>{row.monthlyDowntimeBudget}</td>
              <td>{row.supportResponseSev1}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>What counts as downtime</h2>
      <p>
        Downtime is any minute during which the run-execution surface is unavailable for at least 50% of authenticated
        Rynora users in a region, as measured by the synthetic monitors driving the public status page. Specifically:
      </p>
      <ul>
        <li>The API returns HTTP 5xx for at least 50% of requests for a single minute, or</li>
        <li>A scheduled or on-demand run cannot be started for at least 5 consecutive minutes, or</li>
        <li>Run-result retrieval (GET runs / GET artefacts) is unavailable for at least 5 consecutive minutes.</li>
      </ul>

      <h2>What does not count as downtime</h2>
      <ul>
        <li>
          <strong>Scheduled maintenance.</strong> Pre-announced via the status page and email at least 7 days in
          advance; held to a maintenance window that does not exceed 4 hours per calendar month. Customers on the
          Enterprise tier can request a custom maintenance window aligned to their change calendar.
        </li>
        <li>
          <strong>Customer-caused outages.</strong> Errors caused by the customer's own infrastructure (their app under
          test being down, their CI runner being misconfigured, their SSO IdP being unreachable), customer-elected beta
          features, or customer's own BYO-LLM provider being rate-limited.
        </li>
        <li>
          <strong>Force majeure.</strong> Major regional cloud-provider outages, internet backbone outages, declared
          civil emergencies. We will still publish the incident and the post-mortem; service credits are at our
          discretion.
        </li>
        <li>
          <strong>Beta or preview features.</strong> Anything explicitly marked as beta or preview in the product. The
          SLA covers the GA surface only.
        </li>
      </ul>

      <h2>Service-credit ladder</h2>
      <p>
        If monthly uptime falls below the contractual target for the tier, the affected org is entitled to a service
        credit on the following month's invoice, calculated as a percentage of the monthly fee.
      </p>
      <table>
        <thead>
          <tr>
            <th>Monthly uptime achieved</th>
            <th>Service credit (% of monthly fee)</th>
          </tr>
        </thead>
        <tbody>
          {SERVICE_CREDIT_LADDER.map((row) => (
            <tr key={row.monthlyUptime}>
              <td>{row.monthlyUptime}</td>
              <td>{row.serviceCredit}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p>
        Service credits are the customer's <strong>sole and exclusive remedy</strong> for missed SLA, subject to the cap
        on liability in the Master Services Agreement. Credits are not redeemable for cash and expire if not used within
        12 months of issuance.
      </p>

      <h2>How to claim a service credit</h2>
      <p>
        Email <a href="mailto:trust@rynora.app">trust@rynora.app</a> within 30 days of the end of the affected month
        with the org ID and a request for credit. We reconcile against the public status page measurements and apply the
        credit on the next invoice. We do not require the customer to provide independent measurements; the status page
        is the source of truth.
      </p>

      <h2>Sev-1 definition and support escalation</h2>
      <ul>
        <li>
          <strong>Sev-1.</strong> Production-impacting; the run-execution surface is unavailable for the customer or a
          security vulnerability has been disclosed in the customer's org. Response times as in the per-tier table.
        </li>
        <li>
          <strong>Sev-2.</strong> Significant degradation, but workaround exists. Response within one business day on
          Team / Business; 4 business hours on Enterprise.
        </li>
        <li>
          <strong>Sev-3.</strong> Non-blocking; functionality is reduced or inconvenient. Response within 3 business
          days.
        </li>
        <li>
          <strong>Sev-4.</strong> Feature requests, documentation, "how do I…?" — handled via the normal support
          channel, no SLA.
        </li>
      </ul>

      <h2>What we publish proactively</h2>
      <ul>
        <li>
          <strong>Real-time status</strong> per component on the <Link to="/status">public status page</Link>.
        </li>
        <li>
          <strong>Incident timeline</strong> for every incident on the status page, with public post-mortems for any
          incident that exceeds 15 minutes.
        </li>
        <li>
          <strong>Monthly uptime reports</strong> in each org's admin console (Enterprise tier).
        </li>
      </ul>

      <h2>Contractual primacy</h2>
      <p>
        This page summarises the SLA terms; the <strong>Master Services Agreement (MSA) / Order Form</strong> between
        Rynora and the customer is the binding source of truth. Where this page and the MSA conflict, the MSA controls.
      </p>
    </LegalLayout>
  );
}
